// src/components/common/AssetSearchInput.js
// Reusable asset autocomplete dropdown — used in all transaction modals
import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Tag } from 'lucide-react';
import { assetsApi } from '../../services/api';
import { useDebounce } from '../../utils/helpers';

// initialAsset: { id, asset_code, asset_name } to show when `value` is
// already set (edit forms), instead of looking it up.
// blockReason(asset): a reason string makes that result unselectable and
// shows why (e.g. the parent picker can't offer a tagged asset).
export default function AssetSearchInput({ value, onChange, placeholder, required, disabled, status = 'active', initialAsset, blockReason }) {
  const [query,    setQuery]   = useState('');
  const [results,  setResults] = useState([]);
  const [open,     setOpen]    = useState(false);
  const [loading,  setLoading] = useState(false);
  const [selected, setSelected]= useState(null);
  const containerRef = useRef(null);
  const debQuery = useDebounce(query, 350);

  // Load asset name if value is pre-set (edit mode)
  useEffect(() => {
    if (value && !selected && initialAsset?.id === value) { setSelected(initialAsset); return; }
    if (value && !selected) {
      assetsApi.list({ limit: 1, search: value }).then(r => {
        const a = r.data?.find(x => x.id === value);
        if (a) setSelected(a);
      }).catch(() => {});
    }
    if (!value) { setSelected(null); setQuery(''); }
  }, [value]);

  // Search
  useEffect(() => {
    if (debQuery.length < 1) { setResults([]); return; }
    setLoading(true);
    assetsApi.list({ search: debQuery, status, limit: 20 })
      .then(r => { setResults(r.data || []); setOpen(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [debQuery, status]);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (asset) => {
    setSelected(asset);
    setQuery('');
    setOpen(false);
    onChange(asset.id, asset);
  };

  const clear = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    onChange('', null);
  };

  const tagColor = t => t === 'rfid' ? '#5A3DBB' : t === 'qr' ? '#1A5FA8' : '#0F8A6E';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {selected ? (
        // Selected state
        <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--primary-light)', border:'1.5px solid var(--primary)', borderRadius:'var(--r-md)', padding:'8px 12px', minHeight:40 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:'var(--fs-sm)', color:'var(--primary)' }}>{selected.asset_code}</div>
            <div style={{ fontSize:'var(--fs-xs)', color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected.asset_name}</div>
          </div>
          {selected.is_tagged && selected.tag_type ? (
            <span style={{ background:tagColor(selected.tag_type)+'18', color:tagColor(selected.tag_type), fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:20, whiteSpace:'nowrap' }}>
              <Tag size={8} style={{marginRight:3}}/>{selected.tag_type.toUpperCase()}
            </span>
          ) : (
            <span style={{ background:'var(--bg)', color:'var(--text-muted)', fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:20, whiteSpace:'nowrap' }}>
              {selected.is_taggable === false ? 'Not taggable' : 'Untagged'}
            </span>
          )}
          {!disabled && (
            <button type="button" onClick={clear} style={{ background:'none', border:'none', cursor:'pointer', padding:2, display:'flex', alignItems:'center', color:'var(--text-muted)' }}>
              <X size={15}/>
            </button>
          )}
        </div>
      ) : (
        // Search input
        <div style={{ position:'relative' }}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none' }} />
          <input
            className="form-control"
            style={{ paddingLeft:32 }}
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => { if (results.length > 0) setOpen(true); }}
            placeholder={placeholder || (required ? 'Search assets… *' : 'Search assets…')}
            disabled={disabled}
            autoComplete="off"
          />
          {loading && <div style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)' }}><div className="spinner" style={{width:14,height:14}}/></div>}
        </div>
      )}

      {/* Dropdown */}
      {open && results.length > 0 && !selected && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'var(--white)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', boxShadow:'0 8px 24px rgba(0,0,0,.10)', zIndex:9999, maxHeight:280, overflowY:'auto' }}>
          {results.map(a => { const blocked = blockReason?.(a); return (
            <div key={a.id} onClick={() => { if (!blocked) select(a); }} title={blocked || undefined}
              style={{ padding:'10px 14px', cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.5 : 1, borderBottom:'1px solid var(--border-light)', display:'flex', alignItems:'center', gap:10, transition:'background .1s' }}
              onMouseEnter={e => { if (!blocked) e.currentTarget.style.background='var(--bg)'; }}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:'var(--fs-xs)', color:'var(--primary)' }}>{a.asset_code}</div>
                <div style={{ fontSize:'var(--fs-xs)', color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.asset_name}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>
                  {[a.major_category_name, a.location_name, a.department_name].filter(Boolean).join(' · ')}
                </div>
                {blocked && <div style={{ fontSize:11, color:'var(--danger)', marginTop:2 }}>{blocked}</div>}
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3, flexShrink:0 }}>
                {a.is_tagged && a.tag_type ? (
                  <span style={{ background:tagColor(a.tag_type)+'18', color:tagColor(a.tag_type), fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:20 }}>
                    {a.tag_type.toUpperCase()}
                  </span>
                ) : (
                  <span style={{ background:'var(--bg)', color:'var(--text-muted)', fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:20 }}>
                    {a.is_taggable === false ? 'Not taggable' : 'Untagged'}
                  </span>
                )}
                <span className={`badge ${a.asset_status==='active'?'badge-green':a.asset_status==='checked_out'?'badge-amber':'badge-red'}`} style={{fontSize:10}}>
                  {a.asset_status}
                </span>
              </div>
            </div>
          ); })}
          {results.length === 20 && (
            <div style={{ padding:'8px 14px', fontSize:'var(--fs-xs)', color:'var(--text-muted)', textAlign:'center', background:'var(--bg)', borderTop:'1px solid var(--border-light)' }}>
              Showing top 20 results — type more to refine
            </div>
          )}
        </div>
      )}

      {open && query.length > 0 && !loading && results.length === 0 && !selected && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'var(--white)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', boxShadow:'0 8px 24px rgba(0,0,0,.10)', zIndex:9999, padding:'14px', textAlign:'center', fontSize:'var(--fs-xs)', color:'var(--text-muted)' }}>
          No assets found for "{query}"
        </div>
      )}
    </div>
  );
}
